import {inject, InjectionToken} from '@angular/core';
import {AjsFactoryType, Gender, PatientDto, PatientHeaderDetailsDto, Union2} from 'webcommon/shared';
import {ImoPatientFactory} from './ImoPatientFactory';

// This is the less specific version of ImoPatient that doesn't have conditionals,
// which allows a class to actually implement it.
// Then, we downgrade a factory to create that class.
// This interface is entirely based off of ImoPatient(.js),
// and the interface needs to be preserved for as long as it's downgraded.
export interface ImoPatientDowngrade {
  readonly name?: string | null;
  readonly age?: string | number | null;
  readonly gender?: string | null;
  readonly standardizedAge: number | null;
  readonly hasAge: boolean;
  readonly standardizedGender: Gender | null;
  readonly hasGender: boolean;
}

export interface ImoPatientDowngradeCtor {
  new(
    name?: string | null,
    ageInYears?: string | number | null,
    gender?: string | null,
  ): ImoPatientDowngrade;
  mapFromApi(patient: Union2<PatientDto, PatientHeaderDetailsDto> | null): ImoPatientDowngrade | null;
}

const _ImoPatientDowngradeToken = new InjectionToken<ImoPatientDowngradeCtor>('ImoPatientDowngradeCtor', {
  providedIn: 'root',
  factory() {
    const imoPatientFactory = inject(ImoPatientFactory);

    class ImoPatientDowngradeImpl implements ImoPatientDowngrade {
      readonly name?: string | null;
      readonly age?: string | number | null;
      readonly gender?: string | null;

      readonly standardizedAge: number | null;
      readonly hasAge: boolean;

      readonly standardizedGender: Gender | null;
      readonly hasGender: boolean;

      constructor(...args: ConstructorParameters<ImoPatientDowngradeCtor>) {
        Object.assign(this, imoPatientFactory.create(...args));
      }

      static mapFromApi: ImoPatientDowngradeCtor['mapFromApi'] = (patient) => {
        if (!patient) {
          return null;
        }

        // have to call new here to make sure we get an instance of the class,
        // instead of just an object with a similar structure
        return new ImoPatientDowngradeImpl(...imoPatientFactory._mapFromApi(patient));
      };
    }
    return ImoPatientDowngradeImpl;
  },
});

export const ImoPatientDowngradeToken = Object.assign(
  _ImoPatientDowngradeToken,
  {
    ajsFactoryName: 'ImoPatient',
  } as const,
) satisfies AjsFactoryType<InjectionToken<ImoPatientDowngradeCtor>>;
